import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Loader2, Smartphone, Sparkles } from "lucide-react";
import { toast } from "sonner";
import AppBrandHeader from "@/components/AppBrandHeader";
import LoginBrandPanel from "@/components/login/LoginBrandPanel";

export default function Login() {
  const { user, sendOtp, verifyOtp, error: authError } = useAuth();
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState("phone");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  if (user && user !== false && user !== null) return <Navigate to="/" replace />;

  const normalizedPhone = phone.replace(/\s+/g, "").replace(/^\+91/, "");

  const onSendOtp = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const ok = await sendOtp(normalizedPhone);
    setLoading(false);
    if (ok) {
      setStep("otp");
      toast.success("OTP sent to your mobile");
    } else {
      setErr(authError || "Could not send OTP");
    }
  };

  const onVerifyOtp = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const ok = await verifyOtp(normalizedPhone, otp.trim());
    setLoading(false);
    if (ok) {
      toast.success("Welcome back!");
      navigate("/", { replace: true });
    } else {
      setErr(authError || "Invalid OTP");
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 bg-white lg:grid-cols-2" data-testid="login-page">
      <LoginBrandPanel />

      <div className="flex min-h-screen flex-col bg-[#F8F9FA]">
        <div className="border-b border-[#E2E8F0] bg-white px-5 py-4 lg:hidden">
          <AppBrandHeader variant="compact" />
        </div>

        <div className="flex flex-1 items-center justify-center px-6 py-10 sm:px-12 lg:px-16">
          <div className="w-full max-w-md" data-testid="login-card">
            <div className="inline-flex items-center gap-2 rounded-full bg-[#FDECEF] px-3 py-1 text-xs font-semibold text-[#C8102E]">
              <Sparkles className="h-3.5 w-3.5" />
              Staff · OTP sign-in
            </div>

            <h1 className="font-heading mt-5 text-3xl font-extrabold tracking-tighter text-[#0F172A] md:text-4xl">
              {step === "phone" ? "Sign in to QFace" : "Verify your number"}
            </h1>
            <p className="mt-2 text-[#475569] leading-relaxed">
              {step === "phone"
                ? "Enter your registered mobile number. We'll send a one-time password to verify your identity."
                : "Enter the 6-digit OTP sent to your mobile."}
            </p>

            {step === "phone" ? (
              <form onSubmit={onSendOtp} className="mt-8 space-y-5" data-testid="login-form">
                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-xs font-bold uppercase tracking-[0.2em] text-[#475569]">
                    Mobile number
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-[#94A3B8]">
                      +91
                    </span>
                    <Input
                      id="phone"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel"
                      placeholder="9876543210"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      required
                      data-testid="login-phone"
                      className="h-12 rounded-xl border-[#E2E8F0] bg-white pl-12 focus:ring-2 focus:ring-[#C8102E]/20"
                    />
                  </div>
                </div>
                {err && (
                  <div
                    className="rounded-xl border border-[#FECDD3] bg-[#FFE4E6] px-3 py-2 text-sm text-[#E11D48]"
                    data-testid="login-error"
                  >
                    {err}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="login-send-otp"
                  className="h-12 w-full rounded-xl bg-[#C8102E] font-semibold text-white hover:bg-[#9F0D24]"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending OTP…
                    </>
                  ) : (
                    "Send OTP"
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={onVerifyOtp} className="mt-8 space-y-5" data-testid="otp-form">
                <div className="flex items-center gap-2 rounded-xl bg-white px-3 py-2.5 text-sm text-[#475569] ring-1 ring-[#E2E8F0]">
                  <Smartphone className="h-4 w-4 shrink-0 text-[#C8102E]" />
                  <span>
                    OTP sent to <strong>+91 {normalizedPhone}</strong>
                  </span>
                  <button
                    type="button"
                    onClick={() => {
                      setStep("phone");
                      setOtp("");
                      setErr("");
                    }}
                    className="ml-auto text-xs font-semibold text-[#C8102E] hover:underline"
                    data-testid="login-change-phone"
                  >
                    Change
                  </button>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="otp" className="text-xs font-bold uppercase tracking-[0.2em] text-[#475569]">
                    One-time password
                  </Label>
                  <Input
                    id="otp"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    placeholder="6-digit OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    required
                    maxLength={6}
                    data-testid="login-otp"
                    className="h-12 rounded-xl border-[#E2E8F0] bg-white text-center text-lg tracking-[0.3em] focus:ring-2 focus:ring-[#C8102E]/20"
                  />
                </div>
                {err && (
                  <div
                    className="rounded-xl border border-[#FECDD3] bg-[#FFE4E6] px-3 py-2 text-sm text-[#E11D48]"
                    data-testid="login-error"
                  >
                    {err}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={loading || otp.length !== 6}
                  data-testid="login-verify-otp"
                  className="h-12 w-full rounded-xl bg-[#C8102E] font-semibold text-white hover:bg-[#9F0D24]"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Verifying…
                    </>
                  ) : (
                    "Verify & sign in"
                  )}
                </Button>
                <button
                  type="button"
                  onClick={onSendOtp}
                  disabled={loading}
                  className="w-full text-sm font-medium text-[#475569] hover:text-[#0F172A]"
                  data-testid="login-resend-otp"
                >
                  Resend OTP
                </button>
              </form>
            )}

            <div className="mt-10 flex items-start gap-3 border-t border-[#E2E8F0] pt-6 text-sm text-[#475569]">
              <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#059669]" />
              <div>
                <div className="font-semibold text-[#0F172A]">Secure staff login</div>
                <div className="mt-1 text-xs">Same OTP flow as MU One Hostels · HRLMS tokens</div>
              </div>
            </div>

            <div className="mt-8 text-[10px] font-bold uppercase tracking-[0.2em] text-[#94A3B8]">
              © Mahindra University · Bahadurpally, Hyderabad
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
